<div class="acal">

  <!-- ─── En-tête ──────────────────────────────────────────────────── -->
  <header class="acal__head">
    <div>
      <h1 class="acal__title">
        <i class="fa-solid fa-calendar-days"></i>
        {{ 'ADMIN_CALENDAR.TITLE' | translate }}
      </h1>
      <p class="acal__sub">{{ 'ADMIN_CALENDAR.SUBTITLE' | translate }}</p>
    </div>
  </header>

  <!-- ─── Statistiques de la période affichée ──────────────────────── -->
  <div class="acal__stats">
    <div class="acal-stat">
      <span class="acal-stat__value">{{ stats().count }}</span>
      <span class="acal-stat__label">{{ 'CALENDAR.STATS.SESSIONS' | translate }}</span>
    </div>
    <div class="acal-stat">
      <span class="acal-stat__value">{{ stats().hours }}</span>
      <span class="acal-stat__label">{{ 'CALENDAR.STATS.HOURS' | translate }}</span>
    </div>
    <div class="acal-stat">
      <span class="acal-stat__value">{{ stats().professors }}</span>
      <span class="acal-stat__label">{{ 'ADMIN_CALENDAR.PROFESSORS' | translate }}</span>
    </div>
    <div class="acal-stat">
      <span class="acal-stat__value">{{ stats().online }}</span>
      <span class="acal-stat__label">{{ 'ADMIN_CALENDAR.ONLINE' | translate }}</span>
    </div>
  </div>

  <!-- ─── Filtres ──────────────────────────────────────────────────── -->
  <div class="acal__filters">
    <label class="acal-filter">
      <span class="acal-filter__label">{{ 'ADMIN_CALENDAR.PROFESSOR' | translate }}</span>
      <select [(ngModel)]="filterProfessorId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'ADMIN_CALENDAR.ALL_PROFESSORS' | translate }}</option>
        @for (p of options().professors; track p.id) {
          <option [ngValue]="p.id">{{ p.fullName }}</option>
        }
      </select>
    </label>

    <label class="acal-filter">
      <span class="acal-filter__label">{{ 'CALENDAR.FILTER.SUBJECT' | translate }}</span>
      <select [(ngModel)]="filterSubjectId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_SUBJECTS' | translate }}</option>
        @for (g of subjectGroups(); track g.pathId) {
          <optgroup [label]="g.pathLabel || ('CALENDAR.CREATE.NO_PATH' | translate)">
            @for (s of g.subjects; track s.id) {
              <option [ngValue]="s.id">{{ subjectLabel(s) }}</option>
            }
          </optgroup>
        }
      </select>
    </label>

    <label class="acal-filter">
      <span class="acal-filter__label">{{ 'CALENDAR.FILTER.GROUP' | translate }}</span>
      <select [(ngModel)]="filterGroupId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_GROUPS' | translate }}</option>
        @for (g of options().groups; track g.id) {
          <option [ngValue]="g.id">{{ g.name }}</option>
        }
      </select>
    </label>

    <label class="acal-check">
      <input type="checkbox" [(ngModel)]="includeCancelled" (ngModelChange)="applyFilters()">
      {{ 'ADMIN_CALENDAR.SHOW_CANCELLED' | translate }}
    </label>

    @if (hasFilters) {
      <button class="acal-filter__clear" type="button" (click)="clearFilters()">
        <i class="fa-solid fa-xmark"></i>
        {{ 'CALENDAR.FILTER.CLEAR' | translate }}
      </button>
    }
  </div>

  <!-- ─── Calendrier ───────────────────────────────────────────────── -->
  <app-sessions-calendar [sessions]="sessions()"
                         [loading]="loading()"
                         [errored]="errored()"
                         [inspection]="true"
                         (rangeChange)="onRange($event)"
                         (select)="onSelect($event)"
                         (retry)="reload()"></app-sessions-calendar>

  <!-- ─── Détail de séance + bilan de présence ─────────────────────── -->
  <dialog #detailDialog class="acal-dialog acal-drawer-host"
          (close)="onDetailClosed()"
          (mousedown)="onPressStart($event)"
          (click)="onBackdropClick($event, detailDialog)">
    @if (selected(); as s) {
    <aside class="acal-drawer" [class]="view.colorClass(s)"
           aria-labelledby="acal-detail-title">
      <button type="button" class="acal-drawer__close" (click)="closeDetail()"
              [attr.aria-label]="'CALENDAR.CLOSE' | translate">
        <i class="fa-solid fa-xmark"></i>
      </button>

      <span class="acal-drawer__kind">
        <i [class]="view.kindIcon(s.sessionKind)"></i>
        {{ view.kindLabelKey(s.sessionKind) | translate }}
      </span>

      <h2 class="acal-drawer__title" id="acal-detail-title">{{ s.title }}</h2>

      <div class="acal-badges">
        <!-- Statut : c'est la première question de l'administration. -->
        <span class="acal-badge" [class]="'acal-badge--' + s.status.toLowerCase()">
          {{ ('ADMIN_CALENDAR.STATUS.' + s.status.toUpperCase()) | translate }}
        </span>
        @if (s.isProduction) {
          <span class="acal-badge acal-badge--production">
            <i class="fa-solid fa-clapperboard"></i>
            {{ 'CALENDAR.PRODUCTION.BADGE' | translate }}
          </span>
        }
        <!-- Inspectée ou non : la même pastille que dans la grille, en toutes lettres. -->
        @if (s.isInspected) {
          <span class="acal-badge acal-badge--inspected">
            <i class="fa-solid fa-circle-check"></i>
            {{ 'INSPECTION.STATE.DONE' | translate }}
          </span>
        } @else if (s.status !== 'Cancelled') {
          <span class="acal-badge acal-badge--to-inspect">
            <i class="fa-solid fa-clock"></i>
            {{ 'INSPECTION.STATE.TODO' | translate }}
          </span>
        }
      </div>

      <ul class="acal-drawer__meta">
        <li>
          <i class="fa-solid fa-calendar"></i>
          {{ start(s) | localizedDate:'EEEE dd MMMM yyyy' }}
        </li>
        <li>
          <i class="fa-solid fa-clock"></i>
          {{ time(s.startTime) }} – {{ time(s.endTime) }}
          <span class="acal-drawer__dim">({{ s.durationMinutes }} {{ 'CALENDAR.MINUTES' | translate }})</span>
        </li>
        @if (s.consultantName) {
          <li><i class="fa-solid fa-chalkboard-user"></i> {{ s.consultantName }}</li>
        }
        <!-- Niveau, matière, groupe, élèves : chacun sous son libellé. -->
        <li><app-session-audience [session]="s"></app-session-audience></li>
        @if (s.location) {
          <li><i class="fa-solid fa-location-dot"></i> {{ s.location }}</li>
        }
        @if (s.meetingUrl) {
          <li>
            <i class="fa-solid fa-video"></i>
            <a class="acal-drawer__link" [href]="s.meetingUrl" target="_blank" rel="noopener">
              {{ s.meetingProvider || ('ADMIN_CALENDAR.MEETING_LINK' | translate) }}
            </a>
          </li>
        }
        @if (s.completedAt) {
          <li>
            <i class="fa-solid fa-circle-check"></i>
            {{ 'ADMIN_CALENDAR.COMPLETED_AT' | translate }}
            {{ s.completedAt | date:'dd/MM/yyyy HH:mm' }}
          </li>
        }
        @if (s.createdAt) {
          <li>
            <i class="fa-solid fa-clock-rotate-left"></i>
            <span class="acal-drawer__dim">
              {{ 'ADMIN_CALENDAR.CREATED_AT' | translate }}
              {{ s.createdAt | date:'dd/MM/yyyy HH:mm' }}
              @if (s.updatedAt) {
                · {{ 'ADMIN_CALENDAR.UPDATED_AT' | translate }}
                {{ s.updatedAt | date:'dd/MM/yyyy HH:mm' }}
              }
            </span>
          </li>
        }
        @if (s.status === 'Cancelled' && s.cancellationReason) {
          <li><i class="fa-solid fa-ban"></i> {{ s.cancellationReason }}</li>
        }
      </ul>

      <!-- Consignes de la séance -->
      @if (s.reason) {
        <div class="acal-block">
          <h3 class="acal-block__title">
            <i class="fa-solid fa-align-left"></i>
            {{ 'ADMIN_CALENDAR.NOTES' | translate }}
          </h3>
          <p class="acal-drawer__notes">{{ s.reason }}</p>
        </div>
      }

      <!-- Ce que le professeur a déclaré : motif d'une séance non tenue,
           observation sur une séance de production. -->
      @if (s.consultantNotes) {
        <div class="acal-block">
          <h3 class="acal-block__title">
            <i class="fa-solid fa-comment-dots"></i>
            {{ 'ADMIN_CALENDAR.PROFESSOR_NOTES' | translate }}
          </h3>
          <p class="acal-drawer__notes">{{ s.consultantNotes }}</p>
        </div>
      }

      <!-- ─── Enregistrements produits par la séance ────────────────── -->
      <section class="acal-block">
        <h3 class="acal-block__title">
          <i class="fa-solid fa-film"></i>
          {{ 'CALENDAR.RECORDING.TITLE' | translate }}
        </h3>

        @for (r of s.recordings ?? []; track r.id) {
          <article class="acal-rec" [class.is-archived]="r.isArchived">
            @if (r.previewUrl && r.category === 'Video') {
              <!-- Lecture depuis le stockage : le flux ne passe pas par l'API. -->
              <video class="acal-rec__player" controls preload="metadata" [src]="r.previewUrl"></video>
            }
            <div class="acal-rec__meta">
              <span class="acal-rec__name">
                {{ r.title }}
                @if (r.isArchived) {
                  <span class="acal-rec__tag acal-rec__tag--archived">
                    {{ 'CALENDAR.RECORDING.ARCHIVED' | translate }}
                  </span>
                }
                @if (!r.isPublic && !r.isArchived) {
                  <span class="acal-rec__tag acal-rec__tag--private">
                    {{ 'CALENDAR.RECORDING.NOT_PUBLISHED' | translate }}
                  </span>
                }
              </span>
              <span class="acal-rec__dim">
                {{ fileSize(r.fileSizeBytes) }}
                @if (r.fileSizeBytes) { · }
                {{ r.createdAt | date:'dd/MM/yyyy' }}
              </span>
            </div>
            @if (r.previewUrl && r.category !== 'Video') {
              <a class="acal-drawer__link" [href]="r.previewUrl" target="_blank" rel="noopener">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
                {{ 'CALENDAR.RECORDING.OPEN' | translate }}
              </a>
            }
            @if (r.externalUrl) {
              <a class="acal-drawer__link" [href]="r.externalUrl" target="_blank" rel="noopener">
                <i class="fa-solid fa-link"></i>
                {{ 'CALENDAR.RECORDING.OPEN_LINK' | translate }}
              </a>
            }
          </article>
        } @empty {
          <p class="acal-drawer__dim">{{ 'CALENDAR.RECORDING.EMPTY' | translate }}</p>
        }
      </section>

      <!-- ─── Bilan de présence, depuis le journal d'accès ─────────── -->
      <section class="acal-attend">
        <h3 class="acal-attend__title">
          <i class="fa-solid fa-clipboard-check"></i>
          {{ 'ADMIN_CALENDAR.ATTENDANCE' | translate }}
        </h3>

        @if (attendanceLoading()) {
          <p class="acal-drawer__dim">{{ 'LOADINGS.LOADING' | translate }}</p>
        } @else {
        @if (attendance(); as att) {
          <div class="acal-verdict" [class]="'acal-verdict--' + att.verdict.toLowerCase()">
            <i [class]="verdictIcon(att.verdict)"></i>
            {{ ('CALENDAR.VERDICT.' + att.verdict.toUpperCase()) | translate }}
          </div>

          @if (att.isOnline) {
            <div class="acal-attend__grid">
              <div class="acal-attend__cell">
                <span class="acal-attend__value">{{ att.presentStudents }} / {{ att.expectedStudents }}</span>
                <span class="acal-attend__label">{{ 'ADMIN_CALENDAR.STUDENTS_PRESENT' | translate }}</span>
              </div>
              <div class="acal-attend__cell">
                <span class="acal-attend__value">
                  @if (att.attendanceRate != null) { {{ att.attendanceRate }}% } @else { — }
                </span>
                <span class="acal-attend__label">{{ 'ADMIN_CALENDAR.ATTENDANCE_RATE' | translate }}</span>
              </div>
              <div class="acal-attend__cell">
                <span class="acal-attend__value">
                  @if (att.professorJoinedAt) {
                    {{ att.professorJoinedAt | date:'HH:mm' }}
                  } @else { — }
                </span>
                <span class="acal-attend__label">{{ 'ADMIN_CALENDAR.PROFESSOR_JOINED' | translate }}</span>
              </div>
            </div>

            @if (att.participants.length) {
              <ul class="acal-part">
                @for (p of att.participants; track p.role + '-' + (p.studentId ?? p.professorId)) {
                  <li class="acal-part__row" [class.is-prof]="p.role === 'Professor'">
                    <i [class]="p.role === 'Professor'
                        ? 'fa-solid fa-chalkboard-user'
                        : 'fa-solid fa-user'"></i>
                    <span class="acal-part__name">{{ p.fullName }}</span>
                    <span class="acal-part__time">
                      {{ p.firstJoinedAt | date:'HH:mm' }}
                      <span class="acal-part__delta">
                        @if (p.minutesFromStart > 0) {
                          +{{ p.minutesFromStart }}′
                        } @else if (p.minutesFromStart < 0) {
                          {{ p.minutesFromStart }}′
                        }
                      </span>
                    </span>
                  </li>
                }
              </ul>
            } @else {
              <p class="acal-drawer__dim">{{ 'ADMIN_CALENDAR.NO_ACCESS' | translate }}</p>
            }
          } @else {
            <p class="acal-drawer__dim">{{ 'ADMIN_CALENDAR.ONSITE_HINT' | translate }}</p>
          }
        } @else {
          <p class="acal-drawer__dim">{{ 'ADMIN_CALENDAR.NO_ATTENDANCE' | translate }}</p>
        }
        }
      </section>
    </aside>
    }
  </dialog>

</div>
